/**
 * EntityAliasesPanel — "Entity Aliases" admin tab (KAN-277).
 *
 * Self-loading panel for managing entity alias mappings used by the
 * MCP entity masking feature. Displays a table of bank/org → alias
 * mappings with inline edit, add, and delete.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { clientService } from '../../services/api/clientService';
import type { EntityAlias, EntityAliasCreate, TradingEntity } from '../../services/api/clientService';
import { bankService } from '../../services/api/bankService';
import type { Bank } from '../../services/api/bankService';
import Toast from '../common/Toast';
import './EntityAliasesPanel.css';

const EntityAliasesPanel: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || '';

  // Data
  const [aliases, setAliases] = useState<EntityAlias[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [entities, setEntities] = useState<TradingEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addEntityType, setAddEntityType] = useState<'bank' | 'organization'>('bank');
  const [addEntityId, setAddEntityId] = useState('');
  const [addAlias, setAddAlias] = useState('');
  const [adding, setAdding] = useState(false);

  // Inline edit
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editAlias, setEditAlias] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const loadData = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const [aliasRes, bankRes, entityRes] = await Promise.all([
        clientService.listEntityAliases(clientId),
        bankService.listBanks(),
        clientService.getTradingEntities(clientId),
      ]);
      if (aliasRes.success && aliasRes.data) {
        setAliases(aliasRes.data);
      }
      if (bankRes.success && bankRes.data) {
        setBanks(bankRes.data);
      }
      if (entityRes.success && entityRes.data) {
        setEntities(entityRes.data);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Banks that don't already have an alias
  const availableBanks = banks.filter(
    (b) => !aliases.some((a) => a.entityType === 'bank' && a.entityId === b.id)
  );

  // Entities that don't already have an alias
  const availableEntities = entities.filter(
    (e) => !aliases.some((a) => a.entityType === 'organization' && a.entityId === e.id)
  );

  const handleAdd = async () => {
    if (!addAlias.trim()) return;

    let realName = '';
    let entityId = addEntityId;
    if (addEntityType === 'bank') {
      const bank = banks.find((b) => b.id === addEntityId);
      if (!bank) return;
      realName = bank.name;
    } else {
      const entity = entities.find((e) => e.id === addEntityId);
      if (!entity) return;
      realName = entity.name;
      entityId = entity.id;
    }

    const data: EntityAliasCreate = {
      entityType: addEntityType,
      entityId,
      realName,
      alias: addAlias.trim(),
    };

    setAdding(true);
    try {
      const res = await clientService.createEntityAlias(clientId, data);
      if (res.success && res.data) {
        setAliases((prev) => [...prev, res.data]);
        setShowAddForm(false);
        setAddEntityType('bank');
        setAddEntityId('');
        setAddAlias('');
        setToast({ message: t('admin.entityAliases.aliasCreated', 'Alias created'), type: 'success' });
      } else {
        setToast({ message: res.message || t('admin.entityAliases.createFailed', 'Failed to create alias'), type: 'error' });
      }
    } catch (e: any) {
      setToast({ message: e.message || t('admin.entityAliases.createFailed', 'Failed to create alias'), type: 'error' });
    } finally {
      setAdding(false);
    }
  };

  const handleEdit = (alias: EntityAlias) => {
    setEditingId(alias.id);
    setEditAlias(alias.alias);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editAlias.trim()) return;

    setSaving(true);
    try {
      const res = await clientService.updateEntityAlias(clientId, editingId, {
        alias: editAlias.trim(),
      });
      if (res.success && res.data) {
        setAliases((prev) =>
          prev.map((a) => (a.id === editingId ? { ...a, alias: res.data.alias } : a))
        );
        setEditingId(null);
        setToast({ message: t('admin.entityAliases.aliasUpdated', 'Alias updated'), type: 'success' });
      } else {
        setToast({ message: res.message || t('admin.entityAliases.updateFailed', 'Failed to update alias'), type: 'error' });
      }
    } catch (e: any) {
      setToast({ message: e.message || t('admin.entityAliases.updateFailed', 'Failed to update alias'), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (aliasId: string) => {
    setDeletingId(aliasId);
    try {
      const res = await clientService.deleteEntityAlias(clientId, aliasId);
      if (res.success) {
        setAliases((prev) => prev.filter((a) => a.id !== aliasId));
        setToast({ message: t('admin.entityAliases.aliasDeleted', 'Alias deleted'), type: 'success' });
      } else {
        setToast({ message: res.message || t('admin.entityAliases.deleteFailed', 'Failed to delete alias'), type: 'error' });
      }
    } catch (e: any) {
      setToast({ message: e.message || t('admin.entityAliases.deleteFailed', 'Failed to delete alias'), type: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="entity-aliases-panel">
        <div className="tab-loading">
          <div className="spinner" />
          <p>{t('common.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="entity-aliases-panel">
        <p className="error-text">{error}</p>
      </div>
    );
  }

  const bankAliases = aliases.filter((a) => a.entityType === 'bank');
  const orgAliases = aliases.filter((a) => a.entityType === 'organization');

  return (
    <div className="entity-aliases-panel">
      <div className="ea-header">
        <div>
          <h3>{t('admin.entityAliases.title', 'Entity Aliases')}</h3>
          <p className="ea-description">
            {t('admin.entityAliases.description', 'Define aliases for counterparties and your organisation. When entity masking is enabled on a Toolkit key, these aliases replace real names in AI tool responses.')}
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            setShowAddForm(true);
            // Default to bank if available, otherwise entity
            if (availableBanks.length > 0) {
              setAddEntityType('bank');
              setAddEntityId(availableBanks[0].id || '');
            } else if (availableEntities.length > 0) {
              setAddEntityType('organization');
              setAddEntityId(availableEntities[0].id);
            }
          }}
          disabled={availableBanks.length === 0 && availableEntities.length === 0}
        >
          {t('admin.entityAliases.addAlias', '+ Add Alias')}
        </button>
      </div>

      {/* Entity aliases */}
      <div className="ea-section">
        <div className="ea-section-header">
          <h4>{t('admin.entityAliases.sectionOrganisation', 'Our Entity')} ({orgAliases.length} / {entities.length})</h4>
          {availableEntities.length > 0 && (
            <button
              className="btn-sm btn-add-section"
              onClick={() => {
                setAddEntityType('organization');
                setAddEntityId(availableEntities[0].id);
                setAddAlias('');
                setShowAddForm(true);
              }}
            >
              +
            </button>
          )}
        </div>
        {showAddForm && addEntityType === 'organization' && (
          <div className="ea-add-form">
            <div className="ea-form-row">
              <label>{t('admin.entityAliases.organisationLabel', 'Entity')}</label>
              <select value={addEntityId} onChange={(e) => setAddEntityId(e.target.value)}>
                {availableEntities.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="ea-form-row">
              <label>{t('admin.entityAliases.aliasLabel', 'Alias')}</label>
              <input
                type="text"
                value={addAlias}
                onChange={(e) => setAddAlias(e.target.value)}
                placeholder={t('admin.entityAliases.aliasPlaceholder', 'e.g. Counterparty Alpha')}
                maxLength={200}
              />
            </div>
            <div className="ea-form-actions">
              <button className="btn btn-primary" onClick={handleAdd} disabled={adding || !addAlias.trim()}>
                {adding ? t('admin.entityAliases.saving', 'Saving...') : t('admin.entityAliases.save', 'Save')}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddForm(false);
                  setAddAlias('');
                }}
              >
                {t('admin.entityAliases.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        )}
        {orgAliases.length === 0 ? (
          <p className="ea-empty">
            {t('admin.entityAliases.noEntityAliases', 'No entity aliases defined. Entities without aliases will show as "[MASKED: no alias]" in masked responses.')}
          </p>
        ) : (
          <table className="ea-table">
            <thead>
              <tr>
                <th>{t('admin.entityAliases.columnRealName', 'Real Name')}</th>
                <th>{t('admin.entityAliases.columnAlias', 'Alias')}</th>
                <th style={{ width: 120 }}>{t('admin.entityAliases.columnActions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {orgAliases
                .sort((a, b) => a.realName.localeCompare(b.realName))
                .map((a) => (
                <tr key={a.id}>
                  <td>{a.realName}</td>
                  <td>
                    {editingId === a.id ? (
                      <input
                        type="text"
                        className="ea-inline-edit"
                        value={editAlias}
                        onChange={(e) => setEditAlias(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
                        autoFocus
                      />
                    ) : (
                      a.alias
                    )}
                  </td>
                  <td>
                    {editingId === a.id ? (
                      <div className="ea-actions">
                        <button className="btn-sm btn-save" onClick={handleSaveEdit} disabled={saving}>
                          {saving ? '...' : t('admin.entityAliases.save', 'Save')}
                        </button>
                        <button className="btn-sm btn-cancel" onClick={() => setEditingId(null)}>
                          {t('admin.entityAliases.cancel', 'Cancel')}
                        </button>
                      </div>
                    ) : (
                      <div className="ea-actions">
                        <button className="btn-sm btn-edit" onClick={() => handleEdit(a)}>
                          {t('admin.entityAliases.edit', 'Edit')}
                        </button>
                        <button
                          className="btn-sm btn-delete"
                          onClick={() => handleDelete(a.id)}
                          disabled={deletingId === a.id}
                        >
                          {deletingId === a.id ? '...' : t('admin.entityAliases.delete', 'Delete')}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Bank aliases */}
      <div className="ea-section">
        <div className="ea-section-header">
          <h4>{t('admin.entityAliases.sectionCounterparties', 'Counterparties')} ({bankAliases.length} / {banks.length})</h4>
          {availableBanks.length > 0 && (
            <button
              className="btn-sm btn-add-section"
              onClick={() => {
                setAddEntityType('bank');
                setAddEntityId(availableBanks[0].id || '');
                setAddAlias('');
                setShowAddForm(true);
              }}
            >
              +
            </button>
          )}
        </div>
        {showAddForm && addEntityType === 'bank' && (
          <div className="ea-add-form">
            <div className="ea-form-row">
              <label>{t('admin.entityAliases.counterpartyLabel', 'Counterparty')}</label>
              <select value={addEntityId} onChange={(e) => setAddEntityId(e.target.value)}>
                {availableBanks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="ea-form-row">
              <label>{t('admin.entityAliases.aliasLabel', 'Alias')}</label>
              <input
                type="text"
                value={addAlias}
                onChange={(e) => setAddAlias(e.target.value)}
                placeholder={t('admin.entityAliases.aliasPlaceholder', 'e.g. Counterparty Alpha')}
                maxLength={200}
              />
            </div>
            <div className="ea-form-actions">
              <button className="btn btn-primary" onClick={handleAdd} disabled={adding || !addAlias.trim()}>
                {adding ? t('admin.entityAliases.saving', 'Saving...') : t('admin.entityAliases.save', 'Save')}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddForm(false);
                  setAddAlias('');
                }}
              >
                {t('admin.entityAliases.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        )}
        {bankAliases.length === 0 ? (
          <p className="ea-empty">
            {t('admin.entityAliases.noCounterpartyAliases', 'No counterparty aliases defined. Banks without aliases will show as "[MASKED: no alias]" in masked responses.')}
          </p>
        ) : (
          <table className="ea-table">
            <thead>
              <tr>
                <th>{t('admin.entityAliases.columnRealName', 'Real Name')}</th>
                <th>{t('admin.entityAliases.columnAlias', 'Alias')}</th>
                <th style={{ width: 120 }}>{t('admin.entityAliases.columnActions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {bankAliases
                .sort((a, b) => a.realName.localeCompare(b.realName))
                .map((a) => (
                  <tr key={a.id}>
                    <td>{a.realName}</td>
                    <td>
                      {editingId === a.id ? (
                        <input
                          type="text"
                          className="ea-inline-edit"
                          value={editAlias}
                          onChange={(e) => setEditAlias(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
                          autoFocus
                        />
                      ) : (
                        a.alias
                      )}
                    </td>
                    <td>
                      {editingId === a.id ? (
                        <div className="ea-actions">
                          <button className="btn-sm btn-save" onClick={handleSaveEdit} disabled={saving}>
                            {saving ? '...' : t('admin.entityAliases.save', 'Save')}
                          </button>
                          <button className="btn-sm btn-cancel" onClick={() => setEditingId(null)}>
                            {t('admin.entityAliases.cancel', 'Cancel')}
                          </button>
                        </div>
                      ) : (
                        <div className="ea-actions">
                          <button className="btn-sm btn-edit" onClick={() => handleEdit(a)}>
                            {t('admin.entityAliases.edit', 'Edit')}
                          </button>
                          <button
                            className="btn-sm btn-delete"
                            onClick={() => handleDelete(a.id)}
                            disabled={deletingId === a.id}
                          >
                            {deletingId === a.id ? '...' : t('admin.entityAliases.delete', 'Delete')}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={!!toast}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default EntityAliasesPanel;
